import { useEffect, useState } from "react";
import { X } from "lucide-react";
import SchoolTypeahead from "./SchoolTypeahead";
import { toSchoolInput } from "./wizardData";
import { ChoiceCard, OnboardingQuestion, SelectionSection } from "./OnboardingQuestion";
import type { SchoolResult } from "@/services/schools";
import type { OnboardingSchoolInput, PipelineStage } from "@/services/pipeline";
import type { OnboardingStepProps } from "./types";

type ProgressStage = PipelineStage;

const STAGE_OPTIONS: {
  stage: ProgressStage;
  label: string;
  description: string;
  icon: string;
}[] = [
  {
    stage: "researching",
    label: "Just getting started",
    description: "No coaches have been contacted yet",
    icon: "01",
  },
  {
    stage: "reached_out",
    label: "Contacted one or more coaches",
    description: "An email or recruiting questionnaire has been sent",
    icon: "02",
  },
  {
    stage: "in_conversation",
    label: "In conversation with coaches",
    description: "A coach has replied, called, or texted",
    icon: "03",
  },
  {
    stage: "visited",
    label: "Visited one or more schools",
    description: "At least one official or unofficial visit is complete",
    icon: "04",
  },
  {
    stage: "offered",
    label: "Received an offer",
    description: "A coach has offered a roster spot or scholarship",
    icon: "05",
  },
];

/** Step 6 — selects the player's furthest recruiting stage and any schools there. */
export default function RecruitingProgressStep({
  profile,
  onValidityChange,
  registerSave,
  setWizardData,
  wizardData,
}: OnboardingStepProps) {
  const savedProgress = wizardData?.recruitingProgress ?? [];
  const [stage, setStage] = useState<ProgressStage | null>(savedProgress[0]?.stage ?? null);
  const [schools, setSchools] = useState<OnboardingSchoolInput[]>(savedProgress);

  useEffect(() => {
    onValidityChange(stage !== null);
    registerSave(null);
    return () => registerSave(null);
  }, [onValidityChange, registerSave, stage]);

  const publish = (nextStage: ProgressStage, nextSchools: OnboardingSchoolInput[]) => {
    setWizardData({
      recruitingProgress:
        nextStage === "researching"
          ? []
          : nextSchools.map(school => ({ ...school, stage: nextStage })),
    });
  };

  const selectStage = (nextStage: ProgressStage) => {
    setStage(nextStage);
    publish(nextStage, schools);
  };

  const addSchool = (result: SchoolResult) => {
    if (!stage || schools.some(school => school.teamId === result.schoolId)) return;
    const nextSchools = [...schools, toSchoolInput(result)];
    setSchools(nextSchools);
    publish(stage, nextSchools);
  };

  const removeSchool = (teamId: string) => {
    if (!stage) return;
    const nextSchools = schools.filter(school => school.teamId !== teamId);
    setSchools(nextSchools);
    publish(stage, nextSchools);
  };

  return (
    <OnboardingQuestion
      eyebrow="Last question"
      title={
        profile.onboardingRole === "supporter"
          ? "Where is the golfer in recruiting?"
          : "Where are you in recruiting?"
      }
      description="Choose the furthest point you’ve reached. I’ll start your plan from there."
      coachCopy="This keeps me from giving you homework you’ve already done."
      coachBadge="06"
    >
      <div className="space-y-7">
        <div className="space-y-3">
          {STAGE_OPTIONS.map(option => (
            <ChoiceCard
              key={option.stage}
              selected={stage === option.stage}
              onClick={() => selectStage(option.stage)}
              icon={option.icon}
              title={option.label}
              description={option.description}
              testId={`progress-card-${option.stage}`}
            />
          ))}
        </div>

        {stage && stage !== "researching" && (
          <SelectionSection
            label="Which schools are at this stage?"
            help="Optional — add the schools you remember, and you can update the list later."
          >
            <div className="rounded-2xl border-2 border-border bg-white p-4 shadow-[0_4px_0_#d7e2d9]">
              {schools.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {schools.map(school => (
                    <span
                      key={school.teamId}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-lime-soft px-3 py-1.5 text-xs font-bold text-forest"
                    >
                      {school.schoolName}
                      <button
                        type="button"
                        aria-label={`Remove ${school.schoolName}`}
                        onClick={() => removeSchool(school.teamId)}
                        className="text-muted-foreground transition-colors hover:text-forest"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <SchoolTypeahead
                onSelect={addSchool}
                excludeTeamIds={schools.map(school => school.teamId)}
                gender={profile.gender}
              />
            </div>
          </SelectionSection>
        )}
      </div>
    </OnboardingQuestion>
  );
}
